/**
 * Mapeamento centralizado de códigos de erro do backend para mensagens amigáveis ao usuário
 *
 * Este arquivo centraliza todas as mensagens de erro específicas do backend,
 * garantindo consistência e facilitando manutenção.
 */

export const BACKEND_ERROR_MESSAGES: Record<string, string> = {
  // Erros de Validação (VAL-*)
  "VAL-2001": "CPF inválido. Verifique o número informado.",
  "VAL-2002": "Número de telefone inválido. Verifique o formato.",
  "VAL-2003": "Email inválido. Verifique o formato do endereço.",
  "VAL-2004": "ID da empresa não fornecido. Verifique sua sessão.",

  // Erros de Negócio (BUS-*)
  "BUS-3001": "Usuário não encontrado. Verifique suas credenciais.",
  "BUS-3002": "Empresa não encontrada. Verifique suas permissões.",
  "BUS-3003": "Campanha não encontrada. Verifique se a campanha ainda existe.",
  "BUS-3004": "Lead não encontrado. Verifique se o contato ainda existe.",
  "BUS-3005":
    "Configurações da empresa não encontradas. Entre em contato com o suporte.",
  "BUS-3006":
    "Erro ao buscar informações do perfil do WhatsApp. Tente novamente mais tarde.",
  "BUS-3007":
    "Erro ao buscar informações do negócio do WhatsApp. Tente novamente mais tarde.",
  "BUS-3008": "Número de telefone inválido. Verifique o formato do número.",
  "BUS-3009": "Erro ao enviar mensagem. Tente novamente mais tarde.",
  "BUS-3010": "Limite diário de mensagens atingido. Tente novamente amanhã.",
  "BUS-3011": "Erro ao fazer upload de mídia. Tente novamente mais tarde.",
  "BUS-3012": "Erro ao criar template. Verifique os dados informados.",
  "BUS-3013":
    "Erro ao processar mensagens recebidas. Tente novamente mais tarde.",
  "BUS-3014": "Mensagem não encontrada. Verifique se a mensagem ainda existe.",
  "BUS-3015": "Erro no processamento da IA. Tente novamente mais tarde.",
  "BUS-3016": "Erro ao atualizar estado da IA. Tente novamente mais tarde.",
  "BUS-3017": "Erro ao buscar templates. Tente novamente mais tarde.",
  "BUS-3018": "Template não atribuído à campanha. Verifique a configuração.",
  "BUS-3019": "Erro ao buscar campanhas. Tente novamente mais tarde.",
  "BUS-3020": "Erro ao buscar templates. Tente novamente mais tarde.",
  "BUS-3021":
    'Token de acesso da Meta expirado ou inválido. Por favor, verifique as credenciais ou atualize os dados na página "Perfil da Empresa".',
  "BUS-3022":
    "Erro na comunicação com a API da Meta. Tente novamente mais tarde.",
  "BUS-3023":
    "Limite de requisições da Meta API excedido. Tente novamente mais tarde.",
  "BUS-3024": "Template não encontrado. Verifique se o template está aprovado.",
  "BUS-3031":
    "A funcionalidade de ligação (Calling API) não está habilitada para este número no WhatsApp Cloud API. Habilite o Calling para o PHONE_NUMBER_ID no Meta antes de enviar o botão de ligação.",
  "BUS-3032":
    "Falta de permissão para realizar esta operação na Meta. Verifique se o seu token possui as permissões necessárias (como business_management, whatsapp_business_management e whatsapp_business_messaging).",

  // Erros de Autenticação (AUTH-*)
  "AUTH-1001": "Credenciais inválidas. Verifique seu email e senha.",
  "AUTH-1002": "Token inválido. Faça login novamente.",
  "AUTH-1003": "Sessão expirada. Faça login novamente.",
  "AUTH-1004": "Acesso não autorizado. Verifique suas permissões.",

  // Erros de Rede (NET-*)
  "NET-0001":
    "Erro de conexão com o servidor. Verifique sua conexão com a internet.",
  "NET-0002": "Timeout na requisição. Tente novamente mais tarde.",
  "NET-0003": "Servidor indisponível. Tente novamente mais tarde.",

  // Erros Internos (REQ-*)
  "REQ-0001": "Erro interno do servidor. Tente novamente mais tarde.",
  "REQ-0002": "Erro de processamento. Tente novamente mais tarde.",
} as const;

/**
 * Extrai o código de erro do backend de uma mensagem de erro
 * @param message Mensagem de erro que pode conter o código
 * @returns Código de erro encontrado ou null
 */
export const extractBackendErrorCode = (message: string): string | null => {
  if (!message || typeof message !== "string") {
    return null;
  }

  // Procura por padrões como "BUS-3021", "VAL-2004", etc.
  const codeMatch = message.match(/\b([A-Z]{3}-\d{4})\b/);
  return codeMatch ? codeMatch[1] : null;
};

/**
 * Obtém mensagem amigável para um código de erro do backend
 * @param code Código de erro do backend
 * @returns Mensagem amigável ou mensagem padrão
 */
export const getBackendErrorMessage = (code: string): string => {
  return BACKEND_ERROR_MESSAGES[code] || "";
};

/**
 * Verifica se um código de erro é recuperável (usuário pode tentar novamente)
 * @param code Código de erro do backend
 * @returns true se o erro é recuperável
 */
export const isRecoverableError = (code: string): boolean => {
  const nonRecoverableCodes = [
    "AUTH-1001", // Credenciais inválidas
    "AUTH-1002", // Token inválido
    "AUTH-1003", // Sessão expirada
    "AUTH-1004", // Acesso não autorizado
    "BUS-3002", // Empresa não encontrada
    "BUS-3005", // Configurações não encontradas
    "BUS-3021", // Token Meta expirado
    "BUS-3032", // Falta de permissão Meta
  ];

  return !nonRecoverableCodes.includes(code);
};
